/**
 * 4975 / 5036 — realtime transport for the internal project chat (PWA).
 *
 * Laravel Echo over the Pusher protocol. Two modes, decided by the SERVER:
 *
 *  - Hosted Pusher Channels: the config carries a cluster and no host.
 *    pusher-js derives ws-<cluster>.pusher.com itself; nothing in this file
 *    names a hostname or a cluster.
 *  - Self-hosted (Soketi): the config carries host/port/tls, which become the
 *    wsHost/wsPort/wssPort options.
 *
 * The config is fetched at runtime from GET api/project-chat/socket-config once
 * a user is logged in. Nothing realtime-related is baked into the build: the
 * env files carry no realtime keys at all, a key rotation needs no PWA
 * release, and the server's `enabled` flag (PUSHER_PWA_ENABLED on the Weldit
 * backend) is the kill switch for every installed PWA at once.
 *
 * Design rules:
 *
 *  - The socket is additive on top of a working polled implementation, so a
 *    socket that is disabled, fails to connect or dies mid-session makes chat
 *    slower, never broken. Callers consult isConnected() to pick an interval:
 *      • Chat.vue (the open conversation) polls at 2 s without a socket and at
 *        20 s with one. Its poll is never switched off.
 *      • The notifier (every screen) polls at 2 s without a socket and runs NO
 *        timer while one is live; onTransportChange() is what re-arms it.
 *
 *  - isConnected() means "live", not merely "open": the socket is connected AND
 *    no channel subscription has failed. A failed subscription on an otherwise
 *    healthy socket would leave the notifier with no events and no timer — a
 *    dead zone — so ANY failure flips isConnected() to false and the fallback
 *    poll re-arms. Only the retry differs: a retryable failure (expired token,
 *    auth endpoint unreachable, socket server misconfigured) gets one fresh
 *    subscription attempt after RETRY_MS; a 403 (not a member, chat switched
 *    off for that project, tenant mismatch) gets none — the channel is dropped
 *    so pusher-js stops re-authorizing it, and it is tried again only on the
 *    next (re)connect. A failure mark is cleared when the channel subscribes,
 *    when the last caller detaches from the thread (the notifier does so once
 *    the server stops listing it), or on (re)connect, which re-attempts every
 *    thread that still has handlers and lets a repeat failure mark it again.
 *
 *  - Handlers are a LIST per thread. Chat.vue and the notifier register for
 *    the same threads; every registration receives the events, and each caller
 *    detaches only its own with the off() that joinThread() returns. Channels
 *    stay joined after a detach — re-joining reuses them.
 *
 *  - Channels are tenant-scoped: tenant.<subdomain>.project-chat.thread.<id>.
 *    Thread ids are per-tenant auto-increments, so without the tenant prefix
 *    one tenant's messages would arrive in another's app. The prefix is the
 *    server's channel_prefix from socket-config (the same string the publisher
 *    names the channel with); the cookie-derived tenant only fills in for an
 *    older server answer without it. The server re-checks the segment in
 *    routes/channels.php.
 *
 *  - Auth uses the PWA's own token endpoint (api/project-chat/socket-auth) and
 *    reads the Passport bearer token from its cookie on EVERY authorization
 *    (pusher-js headersProvider), so a token refreshed mid-session is what the
 *    next reconnect uses, not the one present when the socket first opened.
 *
 * Exposed as Vue.prototype.$projectChatRt.
 */
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import VueCookie from 'vue-cookie'
import { hostAppApi } from './axios_settings'

/** One fresh subscription attempt this long after a retryable channel failure. */
const RETRY_MS = 30000
/** A failed config fetch is retried on the next connect() attempt after this. */
const CONFIG_RETRY_MS = 60000

const state = {
  echo: null,
  connected: false,
  connectPromise: null,
  // Server-provided realtime config, cached for the session (see fetchConfig).
  config: null,
  configPromise: null,
  configRetryAt: 0,
  // Bumped on disconnect() so an in-flight fetch/connect from the previous
  // login cannot land in the next one.
  generation: 0,
  tenant: null,
  // 'tenant.<sub>.project-chat.thread.' — fixed by openSocket() from the
  // server's channel_prefix before any channel is joined.
  channelPrefix: null,
  // channelName -> channel we hold listeners on.
  joined: {},
  // channelName -> threadId for subscriptions that failed for a retryable
  // reason. Non-empty means "not live" — see isLive().
  failed: {},
  // channelName -> true once its single retry has been spent.
  retried: {},
  retryTimer: null,
  // threadId -> [ { created, updated, deleted, read } ], one entry per caller.
  handlers: {},
  // Thread the user is currently looking at in Chat.vue, so the notifier can
  // skip toasting what is already on screen.
  viewing: null,
  // Called whenever live-ness flips, so a poller can re-evaluate its interval
  // the moment the transport changes rather than on its next tick. Required
  // because the notifier stops its timer outright while a socket is live:
  // without this it would never learn the socket died and would go silent.
  onTransport: []
}

const debug = process.env.VUE_APP_ENVIRONMENT === 'local'

/**
 * Local-only tracing.
 *
 * Deliberately console.log, NOT console.debug: main.js configures Sentry with
 * captureConsoleIntegration({ levels: ['debug'] }), so console.debug is shipped
 * to Sentry as an event. A chat socket reconnecting is not an incident, and at
 * one line per event it would flood the project's quota.
 */
function log (...args) {
  if (debug && window.console) {
    console.log('[project-chat:rt]', ...args)
  }
}

/** The socket is open and every subscription that should be up is up. */
function isLive () {
  return state.connected && Object.keys(state.failed).length === 0
}

/** Tell every listener the transport state changed. */
function transportChanged () {
  const live = isLive()
  state.onTransport.forEach(fn => {
    try { fn(live) } catch (e) { /* a listener must not break others */ }
  })
}

function readTokenCookie () {
  return VueCookie.get(process.env.VUE_APP_TOKEN) || null
}

/** Tenant subdomain, resolved the same way getHostRefApi does. */
function resolveTenant () {
  const hostDomain = process.env.VUE_APP_STORE_HOST_DOMAIN || ''
  let hostRef = VueCookie.get('WelditPwa_host-ref') || ''
  if (!hostRef) return null
  // The cookie may or may not already carry the TLD; the channel name uses the
  // bare subdomain, which is what the server compares against route('account').
  if (hostDomain && hostRef.includes(hostDomain)) {
    hostRef = hostRef.replace(hostDomain, '')
  }
  return hostRef || null
}

/** `<scheme>://<tenant><suffix>/api/`, built exactly like getHostRefApi. */
function apiBase () {
  const scheme = process.env.VUE_APP_URL_SCHEME
  const hostDomain = process.env.VUE_APP_STORE_HOST_DOMAIN || ''
  let hostRef = VueCookie.get('WelditPwa_host-ref') || ''
  if (!hostRef.includes(hostDomain)) hostRef += hostDomain

  return `${scheme === undefined ? '' : scheme + ':'}//${hostRef}/api/`
}

/** Absolute URL of the token-authenticated channel-auth endpoint. */
function authEndpoint () {
  return apiBase() + 'project-chat/socket-auth'
}

/** Absolute URL of the runtime realtime config. */
function configEndpoint () {
  return apiBase() + 'project-chat/socket-config'
}

/** `<prefix><threadId>`; the prefix is fixed by openSocket() before any join. */
function channelName (threadId) {
  return `${state.channelPrefix || defaultChannelPrefix(state.tenant)}${threadId}`
}

/** What channel_prefix looks like when the server does not send one. */
function defaultChannelPrefix (tenant) {
  return `tenant.${tenant}.project-chat.thread.`
}

/**
 * Hosted Pusher Channels vs self-hosted. No host, or a *.pusher.com host, is
 * hosted: pusher-js derives ws-<cluster>.pusher.com from the cluster and must
 * NOT be given wsHost/wsPort. Any other host is a server we dial by name.
 */
function isHostedPusher (cfg) {
  if (!cfg.host) return true

  return /(^|\.)pusher\.com$/i.test(cfg.host)
}

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Shape as served by ProjectChatAPIController::socketConfig(). Everything is
 * optional on this side so an older or partial server answer degrades to
 * "disabled" rather than to an exception.
 */
function normaliseConfig (data) {
  const d = data && typeof data === 'object' ? data : {}

  return {
    enabled: !!d.enabled,
    key: d.key || null,
    cluster: d.cluster || null,
    host: d.host || null,
    port: d.port ? Number(d.port) : null,
    tls: d.tls !== false,
    tenant: d.tenant || null,
    auth_url: d.auth_url || null,
    // channelName() appends the thread id directly, so the prefix must end in
    // a dot whether or not the server's did.
    channel_prefix: d.channel_prefix ? String(d.channel_prefix).replace(/\.?$/, '.') : null
  }
}

/**
 * Fetch the realtime config once per session and hand every caller the same
 * promise while it is in flight.
 *
 * Goes through the shared hostAppApi instance (no circular import: that module
 * only depends on axios and vue-cookie), so the bearer token, the is_pwa query
 * params and the 401 → logout handling all apply exactly as for any other API
 * call. A failed fetch (offline start, server error) is cached as "disabled"
 * for CONFIG_RETRY_MS and then tried again on the next connect() — polling is
 * in charge in the meantime, which is the right answer offline anyway.
 */
function fetchConfig () {
  const stale = state.config && state.config.transient && Date.now() >= state.configRetryAt
  if (state.config && !stale) return Promise.resolve(state.config)
  if (state.configPromise) return state.configPromise

  const gen = state.generation

  state.configPromise = hostAppApi.get(configEndpoint())
    .then(response => normaliseConfig(response && response.data))
    .catch(e => {
      log('config fetch failed — polling only for now', e)
      return { enabled: false, transient: true }
    })
    .then(cfg => {
      // A logout happened while this was in flight: the answer belongs to the
      // previous user. Do not cache it.
      if (gen !== state.generation) return { enabled: false, transient: true }

      if (cfg.transient) state.configRetryAt = Date.now() + CONFIG_RETRY_MS
      state.config = cfg
      state.configPromise = null
      return cfg
    })

  return state.configPromise
}

/** Drop the cached config and fetch it afresh. Does not touch a live socket. */
function refreshConfig () {
  state.config = null
  state.configPromise = null
  state.configRetryAt = 0

  return fetchConfig()
}

/* -------------------------------------------------------------------------- */
/* Connection                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Connect, if the server says so. Safe to call repeatedly — a live connection
 * is kept and concurrent callers share one attempt. Resolves false when
 * realtime is unavailable, so callers keep polling fast.
 */
function connect () {
  if (state.echo) return Promise.resolve(true)
  if (state.connectPromise) return state.connectPromise

  const gen = state.generation

  const attempt = fetchConfig()
    .then(cfg => {
      if (state.connectPromise === attempt) state.connectPromise = null
      if (gen !== state.generation) return false
      if (state.echo) return true

      return openSocket(cfg)
    })
    .catch(e => {
      if (state.connectPromise === attempt) state.connectPromise = null
      log('connect failed', e)

      return false
    })
  state.connectPromise = attempt

  return attempt
}

/** Build the Echo instance from the fetched config. Returns whether it exists. */
function openSocket (cfg) {
  if (!cfg.enabled || !cfg.key) {
    log('realtime disabled by server config — polling only')
    return false
  }

  const hosted = isHostedPusher(cfg)
  // Hosted mode needs a cluster to derive the endpoint from; self-hosted mode
  // needs the port to dial. Neither is guessed: a partial answer is "disabled".
  if (hosted ? !cfg.cluster : !cfg.port) {
    log('incomplete server config — polling only', cfg)
    return false
  }

  const tenant = resolveTenant()
  if (!tenant) {
    log('no tenant resolved yet')
    return false
  }
  if (cfg.tenant && cfg.tenant !== tenant) {
    // Both derive from the same host by construction. Should they ever
    // disagree, the server's channel_prefix wins (it is what the publisher
    // names the channel and what routes/channels.php checks) and this line is
    // the clue.
    log('tenant mismatch: server says', cfg.tenant, 'cookie says', tenant)
  }
  state.tenant = tenant
  state.channelPrefix = cfg.channel_prefix || defaultChannelPrefix(cfg.tenant || tenant)

  if (!readTokenCookie()) {
    log('no auth token yet')
    return false
  }

  window.Pusher = Pusher

  const options = {
    broadcaster: 'pusher',
    key: cfg.key,
    // Load-bearing in hosted mode (pusher-js derives the host from it), inert
    // in self-hosted mode where wsHost is given.
    cluster: cfg.cluster || 'eu',
    // Hosted Pusher is TLS-only; self-hosted follows the server's tls flag.
    forceTLS: hosted || cfg.tls !== false,
    disableStats: true,
    // Websocket only: Pusher's HTTP fallbacks would silently long-poll a
    // server that may not support them.
    enabledTransports: ['ws', 'wss'],
    // 5089: laravel-echo would otherwise look for a global axios / jQuery /
    // Vue.http and stamp X-Socket-Id on every request they make. The API's
    // CORS allow-list (nginx in production) does not include that header, so
    // the socket id must ONLY ever travel as the socket_id query parameter
    // (App.vue interceptor). No such global exists in this app today; this
    // makes the header impossible rather than merely absent.
    withoutInterceptors: true,
    // pusher-js ≥ 8 channel authorization. headersProvider runs on every
    // authorization request, so the token is read fresh each time.
    channelAuthorization: {
      transport: 'ajax',
      endpoint: cfg.auth_url || authEndpoint(),
      headersProvider: () => {
        const headers = { Accept: 'application/json' }
        const token = readTokenCookie()
        if (token) headers.Authorization = `Bearer ${token}`
        return headers
      }
    }
  }

  // Self-hosted mode only. In hosted mode these keys must be ABSENT so
  // pusher-js derives ws-<cluster>.pusher.com. The port is the server's;
  // nothing here knows what a socket server usually listens on.
  if (!hosted) {
    options.wsHost = cfg.host
    options.wsPort = cfg.port
    options.wssPort = cfg.port
  }

  try {
    state.echo = new Echo(options)
  } catch (e) {
    log('Echo init failed', e)
    state.echo = null
    return false
  }

  bindConnectionEvents()
  log('socket opened', hosted ? `hosted cluster ${options.cluster}` : `self-hosted ${cfg.host}:${cfg.port}`)

  // Handlers registered while there was no socket (config fetch had failed,
  // no token yet, …) are waiting for exactly this moment.
  Object.keys(state.handlers).forEach(threadId => subscribeChannel(threadId))

  return true
}

function bindConnectionEvents () {
  try {
    const conn = state.echo.connector.pusher.connection
    conn.bind('connected', () => {
      state.connected = true
      // pusher-js re-authorizes every held channel on (re)connect. Start the
      // failure bookkeeping afresh; ch.error() repopulates it if needed.
      state.failed = {}
      state.retried = {}
      // A channel dropped after a 403 is NOT held by pusher-js any more, so it
      // must be re-attempted here or the thread would sit with handlers, no
      // channel and no failure mark — the dead zone. Idempotent for the rest.
      Object.keys(state.handlers).forEach(threadId => subscribeChannel(threadId))
      log('connected')
      transportChanged()
    })
    ;['unavailable', 'failed', 'disconnected', 'connecting'].forEach(s => {
      conn.bind(s, () => {
        if (state.connected) log('lost connection:', s)
        state.connected = false
        transportChanged()
      })
    })
    conn.bind('error', err => log('error', err))
  } catch (e) {
    log('could not bind connection events', e)
  }
}

/* -------------------------------------------------------------------------- */
/* Channels                                                                   */
/* -------------------------------------------------------------------------- */

/** Fan one event out to every handler registered for the thread. */
function dispatch (action, threadId, payload) {
  const list = state.handlers[threadId]
  if (!list || !list.length) return

  // Iterate a copy: a handler may detach itself while we run.
  list.slice().forEach(h => {
    if (typeof h[action] !== 'function') return
    try { h[action](payload) } catch (e) { log('handler failed', action, e) }
  })
}

/** Hold a channel for a thread. Idempotent while the channel is held. */
function subscribeChannel (threadId) {
  if (!state.echo) return

  const name = channelName(threadId)
  if (state.joined[name]) return

  try {
    const ch = state.echo.private(name)
    state.joined[name] = ch
    log('joining', name)

    ;['created', 'updated', 'deleted', 'read'].forEach(action => {
      ch.listen(`.message.${action}`, payload => {
        log('event', action, payload)
        dispatch(action, (payload && payload.thread_id) || threadId, payload)
      })
    })

    ch.subscribed(() => forgetFailure(name))

    ch.error(err => onChannelError(name, threadId, err))
  } catch (e) {
    log('join failed', name, e)
  }
}

/**
 * pusher:subscription_error. `err.status` is the HTTP status of the auth
 * request when there was one (0 for a network failure), absent otherwise.
 */
function onChannelError (name, threadId, err) {
  const status = err && typeof err.status === 'number' ? err.status : null
  log('channel error', name, status, err)

  // Any failure means this thread has no working channel: mark it so isLive()
  // goes false and the fallback poll takes over — see the header.
  state.failed[name] = threadId

  if (status === 403) {
    // Not entitled, or the server thinks so. Let go of the channel so pusher-js
    // does not re-authorize it on every reconnect, and spend the retry: a
    // denial does not fix itself in RETRY_MS. The next (re)connect, or a later
    // joinThread() (the user opens the chat again), tries afresh.
    dropChannel(name)
    state.retried[name] = true
    transportChanged()
    return
  }

  transportChanged()
  scheduleRetry()
}

/** Drop the failure bookkeeping for one channel and tell the pollers. */
function forgetFailure (name) {
  delete state.retried[name]
  if (!state.failed[name]) return

  delete state.failed[name]
  transportChanged()
}

/** Forget a channel on both sides so a re-join starts a fresh subscription. */
function dropChannel (name) {
  if (state.echo) {
    try { state.echo.leave(name) } catch (e) { /* ignore */ }
  }
  delete state.joined[name]
}

function scheduleRetry () {
  if (state.retryTimer) return
  state.retryTimer = setTimeout(() => {
    state.retryTimer = null
    retryFailed()
  }, RETRY_MS)
}

/**
 * One fresh subscription attempt per failed channel — the token may have been
 * refreshed or the server-side problem fixed in the meantime. Exactly one: a
 * second failure leaves the channel in `failed`, so the fallback poll stays in
 * charge until the next (re)connect resets the bookkeeping.
 */
function retryFailed () {
  if (!state.echo) return

  Object.keys(state.failed).forEach(name => {
    if (state.retried[name]) return
    state.retried[name] = true

    const threadId = state.failed[name]
    log('retrying subscription', name)
    dropChannel(name)
    subscribeChannel(threadId)
  })
}

/**
 * Register handlers for one thread and make sure its channel is held.
 * `handlers` is { created, updated, deleted, read }, all optional. Returns an
 * off() that removes exactly these handlers and nothing else — the channel
 * stays joined for the other callers (and for a later re-join).
 */
function joinThread (threadId, handlers) {
  if (!threadId) return () => {}

  const entry = handlers || {}
  const key = String(threadId)
  if (!state.handlers[key]) state.handlers[key] = []
  state.handlers[key].push(entry)

  // Registration is synchronous; the socket work is not. connect() is a no-op
  // once a socket exists, so this is cheap to call for every thread.
  connect().then(ok => {
    if (ok) subscribeChannel(threadId)
  })

  let detached = false

  return () => {
    if (detached) return
    detached = true
    const list = state.handlers[key]
    if (!list) return
    const i = list.indexOf(entry)
    if (i >= 0) list.splice(i, 1)
    if (list.length) return

    delete state.handlers[key]
    // Nobody is listening to this thread any more, so a failure on its channel
    // must not hold the fallback poll open: this is how a 403 for a thread the
    // user was removed from stops counting once the notifier lets go of it.
    forgetFailure(channelName(key))
  }
}

function leaveAll () {
  if (state.echo) {
    Object.keys(state.joined).forEach(name => {
      try { state.echo.leave(name) } catch (e) { /* ignore */ }
    })
  }
  state.joined = {}
  state.handlers = {}
  state.failed = {}
  state.retried = {}
  if (state.retryTimer) {
    clearTimeout(state.retryTimer)
    state.retryTimer = null
  }
}

/** Full teardown, used on logout so the next user does not inherit channels. */
function disconnect () {
  state.generation += 1

  leaveAll()
  if (state.echo) {
    try { state.echo.disconnect() } catch (e) { /* ignore */ }
  }
  state.echo = null
  state.connectPromise = null
  // Forget the config too: the next login may be another tenant, or the server
  // may have flipped the switch in the meantime.
  state.config = null
  state.configPromise = null
  state.configRetryAt = 0
  state.viewing = null

  const wasConnected = state.connected
  state.connected = false
  state.tenant = null
  state.channelPrefix = null
  // Only announce a real transition. Callers react to transport changes (the
  // notifier re-syncs when a socket comes back), and on logout pcnStop() has
  // already detached — telling it "socket down" then would be harmless, but a
  // spurious event is still noise.
  if (wasConnected) transportChanged()
}

/** pusher-js socket id; App.vue sends it as the socket_id query parameter so the server skips this connection. */
function socketId () {
  try {
    return (state.echo && state.echo.socketId()) || null
  } catch (e) {
    return null
  }
}

const api = {
  connect,
  joinThread,
  leaveAll,
  disconnect,
  refreshConfig,
  socketId,

  /** Live: socket connected and no retryable subscription failure pending. */
  isConnected: () => isLive(),
  /** Raw socket state, regardless of subscription health. Diagnostics only. */
  isSocketOpen: () => state.connected,

  /** Chat.vue reports the thread on screen; null when none. */
  setViewing (threadId) {
    state.viewing = threadId ? String(threadId) : null
  },
  isViewing (threadId) {
    return state.viewing !== null && state.viewing === String(threadId)
  },

  /**
   * Register a callback fired whenever live-ness flips (socket connects or
   * drops, a subscription fails or recovers). Returns an unsubscribe function —
   * call it on teardown so a remounted component does not leave a stale
   * listener behind.
   */
  onTransportChange (fn) {
    if (typeof fn !== 'function') return () => {}
    state.onTransport.push(fn)

    return () => {
      state.onTransport = state.onTransport.filter(f => f !== fn)
    }
  }
}

export default {
  install (Vue) {
    Vue.prototype.$projectChatRt = api
  }
}

export { api as projectChatRt }
